import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { EvidenceItem, EvidenceViewer } from "@/components/topshop/TopShopChallengeEvidence";

export interface SponsorShop { application_id: string; full_name: string | null; business_name: string | null; community: string | null; parish: string | null }

interface ChallengeRow {
  focus_area_id: string; number: string; title: string; status: string; submitted_at: string | null;
  answers: { question: string; answer: string | null }[]; evidence: EvidenceItem[]; score: number | null; max: number | null;
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function SponsorChallengesSheet({ shop, onClose }: { shop: SponsorShop | null; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["sponsor", "challenges", shop?.application_id],
    enabled: !!shop?.application_id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_top_shop_challenge_submissions", { p_application_id: shop!.application_id });
      if (error) throw error;
      return (data ?? []) as unknown as ChallengeRow[];
    },
  });

  return (
    <Sheet open={!!shop} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {shop && (
          <div className="space-y-6">
            <SheetHeader>
              <SheetTitle>{shop.business_name}</SheetTitle>
              <SheetDescription>{[shop.full_name, shop.community, shop.parish].filter(Boolean).join(" | ")}</SheetDescription>
            </SheetHeader>
            {isLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : error ? (
              <p className="text-sm text-destructive">We could not load this shop's challenges.</p>
            ) : (data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">This shop has not marked any challenges ready for scoring yet.</p>
            ) : (
              (data ?? []).map((c) => (
                <section key={c.focus_area_id} className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold"><span className="text-primary tabular-nums">{c.number}</span> {c.title}</h3>
                    <span className="shrink-0 rounded-md bg-muted px-2.5 py-0.5 text-xs font-semibold">
                      {c.status === "reviewed" ? "Reviewed" : "Ready for scoring"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.submitted_at && <>Submitted {format(new Date(c.submitted_at), "MMM d, yyyy")}</>}
                    {c.score !== null && c.max !== null && <> | Score: {fmt(Number(c.score))} of {fmt(Number(c.max))}</>}
                  </p>
                  {c.answers.length > 0 && (
                    <dl className="space-y-2">
                      {c.answers.map((a, i) => (
                        <div key={i}>
                          <dt className="text-sm font-medium">{a.question}</dt>
                          <dd className="whitespace-pre-line text-sm text-muted-foreground">{a.answer || "No answer"}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <EvidenceViewer items={c.evidence} />
                </section>
              ))
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
